export class FoodApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** Create a draft order. */
    create(input) {
        return this.client.request({ method: 'POST', path: '/v1/food/orders', body: input, auth: true });
    }
    /** Read an order snapshot + revision + its versioned event stream. */
    get(orderId) {
        return this.client.request({ method: 'GET', path: `/v1/food/orders/${encodeURIComponent(orderId)}`, auth: true });
    }
    /** The ops/merchant worklist: orders in a status, newest first. */
    listByStatus(status) {
        return this.client.request({ method: 'GET', path: `/v1/food/orders?status=${encodeURIComponent(status)}`, auth: true });
    }
    /**
     * The worklist over several statuses at once, optionally narrowed to one merchant (D372) — the merchant queue asks
     * `submitted, accepted, preparing, ready` for its own merchant. Newest first; `truncated` says a page was cut.
     */
    list(query) {
        const params = new URLSearchParams({ statuses: query.statuses.join(',') });
        if (query.merchantId !== undefined)
            params.set('merchantId', query.merchantId);
        if (query.market !== undefined && query.market !== '')
            params.set('market', query.market);
        if (query.limit !== undefined)
            params.set('limit', String(query.limit));
        return this.client.request({ method: 'GET', path: `/v1/food/orders?${params.toString()}`, auth: true });
    }
    /** D447 (M-05): the store's order history — every status, a window, newest first, paged by an opaque cursor. */
    history(query = {}) {
        const params = new URLSearchParams();
        if (query.merchantId !== undefined)
            params.set('merchantId', query.merchantId);
        if (query.from !== undefined)
            params.set('from', query.from);
        if (query.to !== undefined)
            params.set('to', query.to);
        if (query.statuses !== undefined && query.statuses.length > 0)
            params.set('statuses', query.statuses.join(','));
        if (query.q !== undefined && query.q.trim() !== '')
            params.set('q', query.q.trim());
        if (query.cursor !== undefined)
            params.set('cursor', query.cursor);
        if (query.limit !== undefined)
            params.set('limit', String(query.limit));
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: `/v1/food/orders/history${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /** D447 (M-06): the courier of a ready order — the stage, who is coming, the ETA and the pickup code. */
    courier(orderId) {
        return this.client.request({ method: 'GET', path: `/v1/food/orders/${encodeURIComponent(orderId)}/courier`, auth: true });
    }
    /**
     * Submit a draft through the fail-closed totals gate. D487 (ADR-0100): the SERVER computes the order's totals (the market's governed fee policy, the store's own
     * delivery terms) and stores its own; a total that disagrees is a 409 `FOOD_TOTALS_SERVER_MISMATCH` carrying `serverTotals` and the `differs` field names, and
     * nothing is stored. The tip is the customer's; a discount no source authorises is refused (`FOOD_DISCOUNT_UNVERIFIED`). `dropoff` is needed only when the
     * store delivers within an area (`DROPOFF_REQUIRED`).
     */
    submit(orderId, totals, dropoff) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/submit`, body: { totals, ...(dropoff !== undefined ? { dropoff } : {}) }, auth: true });
    }
    /** Merchant accepts a submitted order. */
    accept(orderId) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/accept`, body: {}, auth: true });
    }
    /** D571: the store approves a rider's request to cancel an order it accepted - the order is cancelled and the rider owes nothing. */
    approveCancelRequest(orderId) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/cancel-request/approve`, body: {}, auth: true });
    }
    /** D571: the store refuses the request (a reason from the closed catalogue): the order continues. */
    refuseCancelRequest(orderId, reasonCode) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/cancel-request/refuse`, body: { reasonCode }, auth: true });
    }
    /** D576: the store answers a pending request with a PARTIAL REFUND (`refundBps` of the total, 1..9999): the rider has a governed window to accept or refuse. */
    proposeRefund(orderId, refundBps) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/cancel-request/propose-refund`, body: { refundBps }, auth: true });
    }
    /** D576: the store extends the preparation estimate frozen at its accept - ONCE, within the governed maximum; the rider is told and may cancel at no cost. */
    extendPrep(orderId, seconds, reasonCode) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/prep-extension`, body: { seconds, reasonCode }, auth: true });
    }
    /** Merchant rejects a submitted order with a stable reason code. */
    reject(orderId, reasonCode) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/reject`, body: { reasonCode }, auth: true });
    }
    /** Walk a plain prep/fulfillment edge (`preparing`|`ready`|`picked_up`|`delivered`). */
    advance(orderId, to) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/status`, body: { to }, auth: true });
    }
    /** Cancel an order before pickup, recording who + why. */
    cancel(orderId, by, reasonCode) {
        return this.client.request({ method: 'POST', path: `/v1/food/orders/${encodeURIComponent(orderId)}/cancel`, body: { by, reasonCode }, auth: true });
    }
}
//# sourceMappingURL=food.js.map